import { readCookieConsent } from "@/components/analytics/browser-attribution";
import { isAnalyticsSuppressedUrl } from "@/lib/analytics/suppressed-paths";

const LIVE_MICROSOFT_UET_HOSTS = new Set([
  "thesitebook.co.uk",
  "www.thesitebook.co.uk",
  "do.thesitebook.co.uk",
]);

const LOCAL_E2E_MICROSOFT_UET_HOSTS = new Set(["localhost", "127.0.0.1"]);

/**
 * Dispatched on `window` by the root MicrosoftUet loader whenever it clears
 * `__tsbMicrosoftUetSuppressed` (always after the granted consent update is
 * queued). Mount-time callers whose `trackMicrosoftAdsEvent` returned false
 * inside the loader's hydration-lagged suppressed window listen for this to
 * retry.
 */
export const MICROSOFT_UET_UNSUPPRESSED_EVENT = "tsb:microsoft-uet-unsuppressed";

export interface MicrosoftUetEventProperties {
  [key: string]: string | number | boolean | null | undefined;
}

function hostnameFromBaseUrl(baseUrl: string | undefined): string | null {
  if (!baseUrl) return null;

  try {
    const url = baseUrl.includes("://") ? baseUrl : `https://${baseUrl}`;
    return new URL(url).hostname.toLowerCase();
  } catch {
    return null;
  }
}

function normalizedHostname(hostname: string | undefined): string | null {
  return hostname ? hostname.toLowerCase() : null;
}

function isLiveMicrosoftUetHost(hostname: string | null): boolean {
  return hostname !== null && LIVE_MICROSOFT_UET_HOSTS.has(hostname);
}

export function shouldLoadMicrosoftUet(
  baseUrl = process.env.NEXT_PUBLIC_APP_BASE_URL,
  browserHostname?: string,
  tagId = process.env.NEXT_PUBLIC_MICROSOFT_UET_TAG_ID,
): boolean {
  if (!tagId?.trim()) return false;

  const normalizedBrowserHostname = normalizedHostname(browserHostname);
  const localE2eEnabled =
    process.env.NEXT_PUBLIC_MICROSOFT_UET_LOCAL_E2E === "1" &&
    normalizedBrowserHostname !== null &&
    LOCAL_E2E_MICROSOFT_UET_HOSTS.has(normalizedBrowserHostname);

  return (
    localE2eEnabled ||
    isLiveMicrosoftUetHost(hostnameFromBaseUrl(baseUrl)) ||
    isLiveMicrosoftUetHost(normalizedBrowserHostname)
  );
}

export function isCurrentUrlSafeForMicrosoftUet(): boolean {
  if (typeof window === "undefined") return false;
  if (window.__tsbMicrosoftUetSuppressed) return false;
  return !isAnalyticsSuppressedUrl(
    window.location.pathname,
    window.location.search,
  );
}

export function pushMicrosoftUetCommand(...args: unknown[]): boolean {
  if (
    typeof window === "undefined" ||
    !window.uetq ||
    typeof window.uetq.push !== "function"
  ) {
    return false;
  }
  window.uetq.push(...args);
  return true;
}

/**
 * Send a consented custom event to Microsoft Advertising.
 *
 * The explicit granted update is deliberately queued immediately before the
 * event. This preserves command ordering if a fast CTA or success-page effect
 * fires after consent but before bat.js has finished loading.
 */
export function trackMicrosoftAdsEvent(
  eventName: string,
  properties: MicrosoftUetEventProperties = {},
): boolean {
  if (
    !eventName ||
    typeof window === "undefined" ||
    readCookieConsent() !== "accepted" ||
    !shouldLoadMicrosoftUet(
      process.env.NEXT_PUBLIC_APP_BASE_URL,
      window.location.hostname,
    ) ||
    !isCurrentUrlSafeForMicrosoftUet()
  ) {
    return false;
  }

  if (
    !pushMicrosoftUetCommand("consent", "update", {
      ad_storage: "granted",
    })
  ) {
    return false;
  }

  return pushMicrosoftUetCommand("event", eventName, properties);
}
